import type { AdminAiRuntimeOperationsSummary } from "../../contracts/admin-read-models";
import type { AdminDashboardLocale } from "./admin-dashboard-locale";
import { pickAdminDashboardLocaleText } from "./admin-dashboard-copy";

export type AdminAiRuntimeSummaryField = keyof AdminAiRuntimeOperationsSummary;

export type AdminAiRuntimeSummaryItemViewModel = {
  id: AdminAiRuntimeSummaryField;
  label: string;
  value: string;
};

export type AdminAiRuntimeSummaryViewModel = {
  title: string;
  sourceGapLabel?: string;
  items: AdminAiRuntimeSummaryItemViewModel[];
};

function formatFieldLabel(
  field: AdminAiRuntimeSummaryField,
  locale: AdminDashboardLocale,
): string {
  switch (field) {
    case "aiProviderRouteLabel":
      return pickAdminDashboardLocaleText(locale, { en: "Provider route", ko: "제공자 경로" });
    case "aiQuotaState":
      return pickAdminDashboardLocaleText(locale, { en: "Quota", ko: "쿼터" });
    case "aiServingAccessState":
      return pickAdminDashboardLocaleText(locale, { en: "Serving access", ko: "접근 상태" });
    case "aiBudgetThresholdState":
      return pickAdminDashboardLocaleText(locale, { en: "Budget", ko: "예산" });
    case "aiProviderLatencyBucket":
      return pickAdminDashboardLocaleText(locale, { en: "Latency", ko: "지연" });
    case "manualReviewFallbackPressure":
      return pickAdminDashboardLocaleText(locale, { en: "Fallback pressure", ko: "폴백 압력" });
    case "aiModelUsageLineage":
      return pickAdminDashboardLocaleText(locale, { en: "Model lineage", ko: "모델 계열" });
    case "aiModelUsageSummary":
      return pickAdminDashboardLocaleText(locale, { en: "Model usage", ko: "사용량" });
    case "aiModelLimitSummary":
      return pickAdminDashboardLocaleText(locale, { en: "Model limit", ko: "한도" });
    default:
      return field;
  }
}

function formatFieldValue(
  value: string,
  locale: AdminDashboardLocale,
): string {
  const normalizedValue = value.trim().toLowerCase();

  if (locale === "ko") {
    switch (normalizedValue) {
      case "ok":
      case "healthy":
        return "정상";
      case "stable":
        return "안정";
      case "elevated":
        return "상승";
      case "warning":
        return "경고";
      case "config_invalid":
        return "구성 오류";
      case "key_material_invalid":
      case "invalid_key_material":
        return "키 구성 오류";
      case "permission_denied":
      case "permission denied":
        return "권한 거부";
      case "near_limit":
      case "near limit":
        return "한도 임박";
      case "within_limit":
      case "within limit":
        return "한도 이내";
      case "over_limit":
      case "over limit":
        return "한도 초과";
      case "unknown":
        return "알 수 없음";
      default:
        return value;
    }
  }

  switch (normalizedValue) {
    case "ok":
    case "healthy":
      return "Healthy";
    case "stable":
      return "Stable";
    case "elevated":
      return "Elevated";
    case "warning":
      return "Warning";
    case "config_invalid":
      return "Config invalid";
    case "key_material_invalid":
    case "invalid_key_material":
      return "Key material invalid";
    case "permission_denied":
    case "permission denied":
      return "Permission denied";
    case "near_limit":
    case "near limit":
      return "Near limit";
    case "within_limit":
    case "within limit":
      return "Within limit";
    case "over_limit":
    case "over limit":
      return "Over limit";
    case "unknown":
      return "Unknown";
    default:
      return value;
  }
}

export function buildAdminAiRuntimeSummaryViewModel(
  summary: AdminAiRuntimeOperationsSummary | undefined,
  locale: AdminDashboardLocale,
  fields: AdminAiRuntimeSummaryField[],
): AdminAiRuntimeSummaryViewModel | undefined {
  if (!summary) {
    return undefined;
  }

  const items = fields.flatMap((field) => {
    const value = summary[field];
    if (!value) {
      return [];
    }

    return [{
      id: field,
      label: formatFieldLabel(field, locale),
      value: formatFieldValue(value, locale),
    }];
  });

  return {
    title: pickAdminDashboardLocaleText(locale, { en: "AI runtime", ko: "AI 런타임" }),
    sourceGapLabel: items.length === 0
      ? pickAdminDashboardLocaleText(locale, {
          en: "Aggregate source not populated yet. Verify the upstream AI runtime summary before treating this state as healthy.",
          ko: "집계 source가 아직 채워지지 않았습니다. 이 상태를 정상으로 보기 전에 upstream AI 런타임 요약 source를 먼저 확인하세요.",
        })
      : undefined,
    items,
  };
}
